import React, { useEffect, useRef, useState } from 'react';
import {
	Box,
	Typography,
	Container,
	Grid,
	Card,
	Avatar,
	Chip,
	Paper,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Divider,
	TextField,
	InputAdornment,
	IconButton,
	Button
} from '@mui/material';
import { motion } from 'framer-motion';
import {
	Storefront as StorefrontIcon,
	ShoppingCart as ShoppingCartIcon,
	Campaign as CampaignIcon,
	SupportAgent as SupportAgentIcon,
	ThumbUpOffAlt as ThumbUpOffAltIcon,
	ThumbDownOffAlt as ThumbDownOffAltIcon,
	Send as SendIcon,
	SmartToy as SmartToyIcon,
	Insights as InsightsIcon,
	LocalShipping as LocalShippingIcon,
	SwapHoriz as SwapHorizIcon,
	Loyalty as LoyaltyIcon,
	Phone as PhoneIcon,
	Psychology as PsychologyIcon,
	Description as DescriptionIcon,
	Groups as GroupsIcon,
	Speed as SpeedIcon,
	Assignment as AssignmentIcon,
	Language as LanguageIcon,
	ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import { AccountCircle as AccountCircleIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';

const RetailCustomerExperienceAssistant = () => {
	// Scroll-to-top on mount
	useEffect(() => { window.scrollTo(0, 0); }, []);

	// Demo chat state
	const [chatMessage, setChatMessage] = useState('');
	const [isTyping, setIsTyping] = useState(false);
	const [chatHistory, setChatHistory] = useState(() => [
		{
			type: 'bot',
			message: 'Merhaba! Mağaza Deneyimi Asistanı’na hoş geldiniz. Size nasıl yardımcı olabilirim?',
			time: '14:00'
		},
		{
			type: 'user',
			message: 'İade sürecini nasıl başlatabilirim?',
			time: '14:01'
		},
		{
			type: 'bot',
			message: 'İade sürecini başlatmak oldukça kolaydır. "Hesabım > Siparişlerim" sekmesinden ilgili siparişi seçip "İade Talebi Oluştur" butonuna tıklayabilirsiniz. Ürünü orijinal ambalajı ve faturasıyla birlikte size iletilen kargo koduyla şubeye bırakmanız yeterlidir. Başka bir konuda yardımcı olabilir miyim?',
			time: '14:01'
		}
	]);
	const chatBoxRef = useRef(null);
	const isDemoLocked = true; // Serbest yazma kapalı; hazır sorular aktif

	// Pazarlama vurgusu
	const marketingSuffix = `—\n**Müşteri Deneyimi Asistanı ile:** daha hızlı destek, daha yüksek memnuniyet, daha fazla dönüşüm.\nDemo & PoC: **info@dcat.com.tr** | (0212) 391 01 80`;
	const enhance = (s) => `${s}\n\n${marketingSuffix}`;

	// Hızlı sorular
	const quickQuestions = [
		'Siparişim nerede?',
		'İade nasıl yapılır?',
		'Kargo ücreti ne kadar?',
		'Mağazalarınız kaçta açılıyor?',
		'Kupon kodumu nasıl kullanırım?',
		'Stok sorgulama'
	];

	// Yanıt motoru (retail)
	const getBotResponse = (text) => {
		const t = text.toLowerCase();

		// Kargo/teslimat durumu
		if (t.includes('kargo') || t.includes('kargom') || t.includes('teslim') || t.includes('siparişimin')) {
			return enhance(`**Kargo / Teslimat Durumu**
	- Demo ortamında kişisel sipariş bilgilerine erişim paylaşılmıyor.
	- Canlıda: **Sipariş No** ile anlık kargo takibi ve tahmini teslimat zamanı gösterilir.
	- SSS: Kargo firması, takip linki, teslimat penceresi, randevulu teslimat.`);
		}

		// İade / değişim politikası
		if (t.includes('iade') || t.includes('değişim')) {
			return enhance(`**İade / Değişim Politikası (Özet)**
	- Fatura tarihinden itibaren genellikle **14 gün** içinde iade/değişim kabul edilir (mağaza politikasına göre değişebilir).
	- Ürün **kullanılmamış**, **etiketli** ve **orijinal ambalajında** olmalıdır.
	- Online siparişlerde: **Hesabım > Siparişler > İade Talebi** adımlarını izleyin.
	- Mağazadan alınan ürünler: İlgili mağazaya fiş/fatura ile başvurun.`);
		}

		// Mağaza stok durumu
		if (t.includes('stok') || t.includes('mağaza')) {
			return enhance(`**Mağaza Stok Durumu**
	- Canlıda: Ürün sayfasında **Mağazada Bul** özelliği ile en yakın mağaza stokları gösterilir.
	- Rezervasyon: Uygun mağazadan **mağazadan teslim al (Click & Collect)** seçeneği ile rezervasyon yapılabilir.`);
		}

		// Sadakat / puanlar
		if (t.includes('sadakat') || t.includes('puan') || t.includes('üyelik')) {
			return enhance(`**Sadakat Programı / Puanlar**
	- Demo’da kişisel puan görüntüleme yapılmaz.
	- Canlıda: **Hesabım > Sadakat** bölümünden puan bakiyesi ve kullanım koşulları görülebilir.
	- Kampanyalarda puanla ödeme / ek puan kazanımı koşulları ayrıntılı belirtilir.`);
		}

		// Kampanyalar
		if (t.includes('kampanya') || t.includes('indirim') || t.includes('fırsat')) {
			return enhance(`**Güncel Kampanyalar**
	- Ana sayfadan **Kampanyalar** sekmesini kontrol edin.
	- Uygulamada bildirim izni açık ise kişiye özel teklifleri anlık alabilirsiniz.
	- Satın alma sepetinde geçerli kuponlar otomatik önerilir.`);
		}

		// Teslimat süresi
		if (t.includes('ne kadar') || t.includes('kaç gün') || t.includes('teslimat süresi')) {
			return enhance(`**Teslimat Süresi**
	- Standart teslimatlar genellikle **1-3 iş günü** sürer (bölge ve taşıyıcıya göre değişir).
	- Aynı gün / ertesi gün seçenekleri belirli şehirlerde mevcuttur.
	- Mağazadan teslim (Click & Collect) çoğu zaman **aynı gün** hazır olur.`);
		}

		// Genel
		return enhance(`Sorunuzu aldım.
	Sipariş, iade, stok, kampanya ve teslimat gibi konularda size yardımcı olabilirim. Hangi konuda detay istersiniz?`);
	};

	const scheduleBotReply = (text) => {
		const reply = getBotResponse(text);
		const delay = Math.min(2000, Math.max(800, reply.length * 25));
		setIsTyping(true);
		setTimeout(() => {
			const time = new Date().toLocaleTimeString().slice(0, 5);
			setChatHistory((prev) => ([...prev, { type: 'bot', message: reply, time }]));
			setIsTyping(false);
		}, delay);
	};

	const handleQuickQuestion = (msg) => {
		const time = new Date().toLocaleTimeString().slice(0, 5);
		setChatHistory((prev) => ([...prev, { type: 'user', message: msg, time }]));
		scheduleBotReply(msg);
	};

	const handleSendMessage = () => {
		if (isDemoLocked) return;
		if (!chatMessage.trim()) return;
		const now = new Date().toLocaleTimeString().slice(0, 5);
		const msg = chatMessage;
		setChatHistory((prev) => ([...prev, { type: 'user', message: msg, time: now }]));
		setChatMessage('');
		scheduleBotReply(msg);
	};

	// Auto scroll messages
	useEffect(() => {
		if (chatBoxRef.current) {
			chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
		}
	}, [chatHistory, isTyping]);

	// Özellikler
	const features = [
		{
			icon: <PsychologyIcon />,
			title: 'Sürekli Öğrenme',
			description: 'AI öğrenme sürecinin birçok kaynaktan ve periodik yapılması.'
		},
		{
			icon: <DescriptionIcon />,
			title: 'Dosya Yükleme',
			description: 'PDF, Word, Resim dosyaları yükleme ve analiz.'
		},
		{
			icon: <GroupsIcon />,
			title: 'Sharepoint Entegrasyonu',
			description: 'Sharepoint Entegrasyonu ile kurumsal veri erişimi.'
		},
		{
			icon: <GroupsIcon />,
			title: 'API Entegrasyonu',
			description: 'API entegrasyonuyla gerçek zamanlı veri alışverişi.'
		},
		{
			icon: <GroupsIcon />,
			title: 'Portal/Web Entegrasyonu',
			description: 'Web sitesi ya da şirket içi portal’den veri okuma ve yanıtlama.'
		},
		{
			icon: <ThumbUpOffAltIcon />,
			title: 'Geri Bildirim',
			description: 'Cevaplar için “faydalı/faydalı değil” bilgisinin alınması ve raporlanması.'
		},
		{
			icon: <SpeedIcon />,
			title: 'Analiz ve Raporlama',
			description: 'Kullanımların raporlanması ve asistanın sürekli geliştirilmesi.'
		},
		{
			icon: <CheckCircleIcon />,
			title: 'Aksiyon Alabilme',
			description: 'Sistemler üzerinden aksiyon alınmasının sağlanması.'
		},
		{
			icon: <SwapHorizIcon />,
			title: 'Self-Servis İade',
			description: 'Adım adım self-servis iade ve değişim süreçleri yönetimi.'
		},
		{
			icon: <LocalShippingIcon />,
			title: 'Anlık Kargo Takibi',
			description: 'Siparişlerin gerçek zamanlı olarak kargo durumunun takibi.'
		}
	];

	// Kullanım alanları
	const useCases = [
		{ title: 'Sipariş ve Teslimat', icon: <ShoppingCartIcon />, items: ['Sipariş durumu sorgulama', 'Kargo takip detayları', 'Teslimat süresi bilgisi', 'Adres güncelleme yardımı'] },
		{ title: 'İade ve Değişim', icon: <SwapHorizIcon />, items: ['İade politikası öğrenme', 'İade talebi oluşturma', 'Değişim prosedürleri', 'Kargo kodu alma'] },
		{ title: 'Ürün ve Mağaza', icon: <StorefrontIcon />, items: ['Mağaza stok sorgulama', 'Ürün özellikleri', 'Mağaza adres/saat bilgisi', 'Click & Collect rehberi'] },
		{ title: 'Kampanya ve Fırsatlar', icon: <LoyaltyIcon />, items: ['Aktif kampanyalar', 'Kupon kodu kullanımı', 'Sadakat puanı sorgulama', 'Kişiye özel teklifler'] }
	];

	return (
		<Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
			{/* Hero */}
			<Box sx={{ background: 'linear-gradient(135deg, #0a172eff 0%, #1a237e 100%)', color: 'white', py: { xs: 8, md: 12 }, position: 'relative', overflow: 'hidden' }}>
				<Container maxWidth="lg">
					<Grid container spacing={4} alignItems="center">
						<Grid item xs={12} md={6}>
							<motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
								<Chip label="Yapay Zeka Destekli Perakende" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', mb: 2, fontWeight: 600 }} />
								<Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '2.5rem' } }}>
									Mağaza Deneyimi Asistanı
								</Typography>
								<Typography variant="h5" sx={{ mb: 3, opacity: 0.95, lineHeight: 1.6 }}>
									Müşterilerinizin sipariş, iade, teslimat ve kampanya sorularını cevaplayan, hem müşteri memnuniyetinin artırılmasını hem de çağrı merkezi iş yükünün hafifletilmesini sağlayan yapay zeka müşteri deneyimi asistanı
								</Typography>
							</motion.div>
						</Grid>
						<Grid item xs={12} md={6}>
							<motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}>
								{/* Demo Chat */}
								<Paper sx={{ p: 3, borderRadius: 3, maxWidth: 400, mx: 'auto', position: 'relative' }}>
									<Chip label="Örnek" size="small" sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#e0e0e0', color: '#424242', fontWeight: 600 }} />
									<Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
										<SmartToyIcon sx={{ color: '#1e3c72', mr: 1 }} />
										<Typography variant="h6" sx={{ color: '#1e3c72', fontWeight: 600 }}>
											Müşteri Deneyim Asistanı
										</Typography>
									</Box>

									{/* Mesajlar */}
									<Box sx={{ height: 450, overflowY: 'auto', mb: 2 }} ref={chatBoxRef}>
										{chatHistory.map((msg, index) => (
											<Box key={index} sx={{ mb: 1 }}>
												<Box sx={{ display: 'flex', justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 1 }}>
													{msg.type !== 'user' && (
														<Avatar sx={{ bgcolor: '#1e3c72', width: 28, height: 28 }}>
															<SmartToyIcon fontSize="small" />
														</Avatar>
													)}
													<Paper sx={{ p: 1.5, display: 'inline-block', maxWidth: '80%', bgcolor: msg.type === 'user' ? '#1e3c72' : '#f5f5f5', color: msg.type === 'user' ? 'white' : 'black', borderRadius: msg.type === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', overflowWrap: 'anywhere', wordBreak: 'break-word' }}>
														<Typography variant="body2" sx={{
															whiteSpace: 'pre-wrap',
															lineHeight: 1.6,
															overflowWrap: 'anywhere',
															wordBreak: 'break-word'
														}}>
															{msg.message}
														</Typography>
													</Paper>
													{msg.type === 'user' && (
														<Avatar sx={{ bgcolor: '#2a5298', width: 28, height: 28 }}>
															<AccountCircleIcon fontSize="small" />
														</Avatar>
													)}
												</Box>
												<Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5, gap: 0.5, justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start' }}>
													<Typography variant="caption" sx={{ opacity: 0.7 }}>{msg.time}</Typography>
													{msg.type === 'bot' && (
														<Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
															<IconButton size="small"><ThumbUpOffAltIcon fontSize="inherit" /></IconButton>
															<IconButton size="small"><ThumbDownOffAltIcon fontSize="inherit" /></IconButton>
														</Box>
													)}
												</Box>
											</Box>
										))}
										{isTyping && (
											<Box sx={{ mb: 1 }}>
												<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
													<Avatar sx={{ bgcolor: '#1e3c72', width: 28, height: 28 }}>
														<SmartToyIcon fontSize="small" />
													</Avatar>
													<Paper sx={{ p: 1.5, display: 'inline-block', bgcolor: '#f5f5f5', color: 'black', borderRadius: '16px 16px 16px 4px' }}>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
															<Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
															<Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.2 }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
															<Box component={motion.div} animate={{ y: [0, -3, 0], opacity: [0.4, 1, 0.4] }} transition={{ duration: 0.9, repeat: Infinity, delay: 0.4 }} sx={{ width: 6, height: 6, bgcolor: '#9e9e9e', borderRadius: '50%' }} />
														</Box>
													</Paper>
												</Box>
												<Typography variant="caption" sx={{ opacity: 0.7, mt: 0.5 }}>Yazıyor…</Typography>
											</Box>
										)}
									</Box>

									{/* Hızlı sorular: girişin üstünde yatay scroll */}
									<Box sx={{ display: 'flex', gap: 1, mb: 1.25, overflowX: 'auto', whiteSpace: 'nowrap', px: 0.5, '&::-webkit-scrollbar': { height: 6 }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#b5b5b5', borderRadius: 8 } }}>
										{quickQuestions.map((q, i) => (
											<Chip key={i} label={q} size="small" clickable onClick={() => handleQuickQuestion(q)} sx={{ fontSize: '0.7rem', mr: 0.5 }} />
										))}
									</Box>

									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
										<Chip label={isDemoLocked ? 'Demo Modu' : 'Canlı'} size="small" sx={{ bgcolor: isDemoLocked ? '#e0e0e0' : 'success.light', fontWeight: 600 }} />
										{isDemoLocked && (
											<Typography variant="caption" sx={{ opacity: 0.7 }}>Hazır sorular aktif • Serbest yazma kapalı</Typography>
										)}
									</Box>
									<TextField
										value={chatMessage}
										onChange={(e) => setChatMessage(e.target.value)}
										onKeyDown={(e) => { if (e.key === 'Enter') handleSendMessage(); }}
										placeholder={isDemoLocked ? 'Örnek demo - giriş devre dışı' : 'Sorunuzu yazın...'}
										size="small"
										fullWidth
										disabled={isDemoLocked}
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton onClick={handleSendMessage} aria-label="mesaj gönder" disabled={isDemoLocked}>
														<SendIcon />
													</IconButton>
												</InputAdornment>
											)
										}}
									/>
									<Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
										Bu asistandaki yanıtlar örnek amaçlıdır.
									</Typography>
								</Paper>
							</motion.div>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Özellikler */}
			<Box sx={{ bgcolor: 'white', py: 8 }}>
				<Container maxWidth="lg">
					<Box textAlign="center" mb={6}>
						<Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
							Ürünün Temel Özellikleri
						</Typography>
						<Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
							Her temas noktasında tutarlı, hızlı ve kişiselleştirilmiş müşteri deneyimi
						</Typography>
					</Box>
					<Grid container spacing={4}>
						{features.map((f, idx) => (
							<Grid item xs={12} sm={6} md={4} key={idx}>
								<motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: idx * 0.1 }} viewport={{ once: true }}>
									<Card sx={{ p: 3, height: '100%', '&:hover': { boxShadow: 6, transform: 'translateY(-4px)' }, transition: 'all 0.3s' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
											<Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}>
												{f.icon}
											</Avatar>
											<Typography variant="h6" sx={{ fontWeight: 700 }}>{f.title}</Typography>
										</Box>
										<Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>{f.description}</Typography>
									</Card>
								</motion.div>
							</Grid>
						))}
					</Grid>
				</Container>
			</Box>

			{/* Kullanım Alanları */}
			<Box sx={{ bgcolor: '#f5f5f5', py: 8 }}>
				<Container maxWidth="lg">
					<Box textAlign="center" mb={6}>
						<Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
							Asistanın Yanıtlayabildiği Konular
						</Typography>
						<Typography variant="h6" color="text.secondary">Müşterilerinizin en çok merak ettiği sorulara anında ve tutarlı yanıtlar</Typography>
					</Box>
					<Grid container spacing={4}>
						{useCases.map((uc, index) => (
							<Grid item xs={12} sm={6} md={3} key={index}>
								<motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
									<Card sx={{ p: 3, height: '100%', bgcolor: 'white', '&:hover': { boxShadow: 6 }, transition: 'all 0.3s' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
											<Avatar sx={{ bgcolor: 'primary.main', mr: 1.5 }}>{uc.icon}</Avatar>
											<Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', fontSize: '1.1rem' }}>{uc.title}</Typography>
										</Box>
										<List dense>
											{uc.items.map((it, i) => (
												<ListItem key={i} sx={{ px: 0, py: 0.5 }}>
													<ListItemIcon sx={{ minWidth: 28 }}><CheckCircleIcon color="success" fontSize="small" /></ListItemIcon>
													<ListItemText primary={it} primaryTypographyProps={{ variant: 'body2' }} />
												</ListItem>
											))}
										</List>
									</Card>
								</motion.div>
							</Grid>
						))}
					</Grid>
				</Container>
			</Box>

			{/* Nasıl Çalışır */}
			<Box sx={{ bgcolor: 'white', py: 8 }}>
				<Container maxWidth="lg">
					<Box textAlign="center" mb={6}>
						<Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>
							Nasıl Çalışır?
						</Typography>
						<Typography variant="h6" color="text.secondary">3 adımda müşteri asistanınız hazır</Typography>
					</Box>
					<Grid container spacing={4}>
						<Grid item xs={12} md={4}>
							<Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
								<Avatar sx={{ bgcolor: '#1e3c72', width: 72, height: 72, mx: 'auto', mb: 3, fontSize: '2rem' }}>1</Avatar>
								<Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>Veriyi Hazırlayın</Typography>
								<Typography variant="body1" color="text.secondary">SSS, iade politikası, teslimat koşulları ve ürün rehberlerini yükleyin.</Typography>
							</Card>
						</Grid>
						<Grid item xs={12} md={4}>
							<Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
								<Avatar sx={{ bgcolor: '#2a5298', width: 72, height: 72, mx: 'auto', mb: 3, fontSize: '2rem' }}>2</Avatar>
								<Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>Yapay Zeka Öğrenir</Typography>
								<Typography variant="body1" color="text.secondary">Dokümanlar ve entegrasyonlarla bilgi tabanı oluşur; diyalog akışları tanımlanır.</Typography>
							</Card>
						</Grid>
						<Grid item xs={12} md={4}>
							<Card sx={{ p: 4, textAlign: 'center', height: '100%' }}>
								<Avatar sx={{ bgcolor: '#1e3c72', width: 72, height: 72, mx: 'auto', mb: 3, fontSize: '2rem' }}>3</Avatar>
								<Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 2 }}>Yayınlayın</Typography>
								<Typography variant="body1" color="text.secondary">Web, mobil uygulama ve mağaza ekranlarında hemen kullanıma alın.</Typography>
							</Card>
						</Grid>
					</Grid>
				</Container>
			</Box>



			{/* Ücretsiz Demo Talep Et CTA */}
			<Box
				component={motion.div}
				initial={{ opacity: 0, y: 50 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true }}
				transition={{ duration: 0.7 }}
				sx={{
					py: { xs: 8, md: 12 },
					background: 'linear-gradient(180deg, #0b1426 0%, #0a1020 100%)',
					textAlign: 'center'
				}}
			>
				<Container maxWidth="md">
					<Typography
						variant="h2"
						sx={{
							fontWeight: 800,
							color: '#ffffff',
							mb: 3,
							fontSize: { xs: '2rem', sm: '2.75rem', md: '3.5rem' },
							textShadow: '2px 0 0 rgba(255,60,80,0.55), -2px 0 0 rgba(60,170,255,0.55)',
							letterSpacing: '-0.5px'
						}}
					>
						Ücretsiz Demo Talep Et
					</Typography>
					<Typography
						sx={{
							color: 'rgba(255,255,255,0.72)',
							maxWidth: 720,
							mx: 'auto',
							mb: 5,
							fontSize: { xs: '1rem', md: '1.125rem' },
							lineHeight: 1.7
						}}
					>
						Yapay zeka çözümlerimizin işletmenize katacağı değeri birlikte keşfedelim. Hemen bizimle iletişime geçin ve uzman ekibimizden demo talep edin.
					</Typography>
					<Button
						size="large"
						endIcon={<ArrowForwardIcon />}
						onClick={() => window.open('https://dcat.com.tr/classic/tr/iletisim', '_blank')}
						sx={{
							px: 5,
							py: 1.75,
							fontSize: '1.05rem',
							fontWeight: 700,
							color: '#fff',
							borderRadius: 999,
							textTransform: 'none',
							background: 'linear-gradient(90deg, #3b5cff 0%, #6a4cff 100%)',
							boxShadow: '0 0 40px rgba(79, 92, 255, 0.55), 0 8px 24px rgba(0,0,0,0.35)',
							transition: 'transform 0.25s ease, box-shadow 0.25s ease',
							'&:hover': {
								background: 'linear-gradient(90deg, #4a6bff 0%, #7a5cff 100%)',
								boxShadow: '0 0 55px rgba(106, 76, 255, 0.7), 0 10px 28px rgba(0,0,0,0.4)',
								transform: 'translateY(-2px)'
							}
						}}
					>
						Hemen Başlayın
					</Button>
				</Container>
			</Box>


		</Box>
	);
};

export default RetailCustomerExperienceAssistant;
